import React, { useMemo } from "react"
import { useSelector } from "react-redux"
import ReactApexChart from "react-apexcharts"

const DoctorSentimentChart = ({ sentimentByDate = [] }) => {
  const mode = useSelector(state => state.Layout.layoutMode)
  const isDark = String(mode).toLowerCase().includes("dark")

  // ---- build chart data from sentimentByDate ----
  const chartData = useMemo(() => {
    if (!Array.isArray(sentimentByDate)) {
      return { categories: [], series: [] }
    }

    // sort by date just in case
    const sorted = [...sentimentByDate].sort((a, b) =>
      (a.date || "").localeCompare(b.date || ""),
    )

    const categories = sorted.map(point => {
      if (!point.date) return "N/A"
      const d = new Date(point.date)
      if (isNaN(d.getTime())) return point.date
      return d.toLocaleDateString("default", {
        day: "2-digit",
        month: "short",
      })
    })

    const data = sorted.map(point => {
      const raw =
        typeof point.sentimentScore === "number"
          ? point.sentimentScore
          : parseFloat(point.sentimentScore || "0")
      const v = isNaN(raw) ? 0 : raw
      return Number(v.toFixed(2))
    })

    return {
      categories,
      series: [
        {
          name: "Sentiment Score",
          data,
        },
      ],
    }
  }, [sentimentByDate])

  const { categories, series } = chartData

  const hasData =
    series &&
    series.length > 0 &&
    Array.isArray(series[0].data) &&
    series[0].data.length > 0

  const options = {
    chart: {
      type: "line",
      toolbar: { show: false },
      zoom: { enabled: false },
      foreColor: isDark ? "#E5E7EB" : "#4B5563",
    },
    stroke: {
      curve: "smooth",
      width: 3,
    },
    dataLabels: {
      enabled: false,
    },
    grid: {
      borderColor: isDark ? "#374151" : "#E5E7EB",
      strokeDashArray: 4,
      yaxis: { lines: { show: true } },
      xaxis: { lines: { show: false } },
    },
    xaxis: {
      categories,
      labels: {
        style: {
          colors: categories.map(() => (isDark ? "#9CA3AF" : "#6B7280")),
        },
      },
      axisBorder: {
        color: isDark ? "#4B5563" : "#D1D5DB",
      },
      axisTicks: {
        color: isDark ? "#4B5563" : "#D1D5DB",
      },
    },
    yaxis: {
      min: 0,
      max:
        hasData && series[0].data.length
          ? Math.max(
              5,
              Math.ceil(
                Math.max.apply(null, series[0].data) + 0.5, // small padding
              ),
            )
          : 5,
      labels: {
        style: {
          colors: [isDark ? "#9CA3AF" : "#6B7280"],
        },
      },
    },
    tooltip: {
      theme: isDark ? "dark" : "light",
      y: {
        formatter: function (val) {
          const num = parseFloat(val)
          if (isNaN(num)) return "0.00"
          return num.toFixed(2)
        },
      },
    },
    markers: {
      size: 4,
    },
    colors: [isDark ? "#60A5FA" : "#2563EB"],
  }

  return (
    <div
      className="br"
      style={{
        width: "100%",
        background: isDark ? "#252F40" : "#fff",
        padding: 24,
        boxShadow: isDark
          ? "0 5px 14px rgba(0,0,0,0.5)"
          : "0 5px 14px rgba(0,0,0,0.1)",
        borderRadius: 12,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <h2
          style={{
            fontSize: "1.25rem",
            fontWeight: 600,
            color: isDark ? "#fff" : "#111827",
          }}
        >
          Sentiment Trend
        </h2>
      </div>

      {/* Chart / Empty State */}
      {hasData ? (
        <ReactApexChart
          options={options}
          series={series}
          type="line"
          height={360}
        />
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: 40,
            color: isDark ? "#9CA3AF" : "#6B7280",
          }}
        >
          No sentiment data available for the selected period
        </div>
      )}
    </div>
  )
}

export default DoctorSentimentChart
